import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileWarning, Send, Loader2, Copy, CheckCircle, Rocket } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API } from './adminConstants';
import { PLAN_CATALOG } from '@/constants/plans';

const PAID_PLANS = PLAN_CATALOG.filter(p => p.id !== 'free' && p.price > 0);

const TAG_LABELS = {
  h1: 'Toque 1h', h24: 'Toque 24h', h72: 'Toque 72h',
  d7: 'Toque 7 dias', d15: 'Toque 15 dias', d25: 'Toque 25 dias',
  w3: 'Aviso: 3 dias p/ bloqueio', w1: 'Aviso: 1 dia p/ bloqueio',
};
const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const AdminPendingDocsTab = () => {
  const { getToken } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, assisted: 0, blocked: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [docInputs, setDocInputs] = useState({});
  const [dueDays, setDueDays] = useState({});
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [activateForm, setActivateForm] = useState({ company_id: '', plan_id: 'enterprise', deadline_days: 30, block_enabled: false });
  const [activating, setActivating] = useState(false);

  const headers = useCallback(() => ({ headers: { Authorization: `Bearer ${getToken()}` } }), [getToken]);
  const fetchData = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/pending-docs`, headers())
      .then(r => setData(r.data))
      .catch(() => toast.error('Erro ao carregar pendências'))
      .finally(() => setLoading(false));
  }, [headers]);
  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    axios.get(`${API}/admin/companies`, headers())
      .then(r => setCompanies((r.data || []).filter(c => !c.archived && c.plan === 'free' && !c.billing_exempt)))
      .catch(() => {});
  }, [headers]);

  const activate = async () => {
    if (!activateForm.company_id) { toast.error('Escolha uma empresa'); return; }
    setActivating(true);
    try {
      const r = await axios.post(`${API}/admin/pending-docs/activate`, activateForm, headers());
      toast.success(`Plano ${r.data.plan_name} liberado! Prazo do CNPJ até ${r.data.deadline}`);
      setActivateForm({ company_id: '', plan_id: 'enterprise', deadline_days: 30, block_enabled: false });
      setSearch('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao ativar plano'); }
    finally { setActivating(false); }
  };

  const filteredCompanies = search.trim().length < 2 ? []
    : companies.filter(c => c.name.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 8);
  const selectedCompany = companies.find(c => c.id === activateForm.company_id);

  const resend = async (item) => {
    setBusy(item.company_id);
    try {
      await axios.post(`${API}/admin/pending-docs/${item.company_id}/resend`, {}, headers());
      toast.success('Link reenviado no WhatsApp!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao reenviar'); }
    finally { setBusy(''); }
  };

  const complete = async (item) => {
    const doc = (docInputs[item.company_id] || '').replace(/\D/g, '');
    if (doc.length !== 11 && doc.length !== 14) { toast.error('Informe um CPF ou CNPJ válido'); return; }
    setBusy(item.company_id);
    try {
      const r = await axios.post(`${API}/admin/pending-docs/${item.company_id}/complete`,
        { cpf_cnpj: doc, due_days: dueDays[item.company_id] || undefined }, headers());
      toast.success(`Boleto gerado! Vence em ${r.data.due_date}`);
      setDocInputs({ ...docInputs, [item.company_id]: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao gerar cobrança'); }
    finally { setBusy(''); }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link de conclusão copiado!');
  };

  return (
    <Card className="border-0 shadow-sm border-l-4 border-l-amber-500" data-testid="pending-docs-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileWarning className="w-5 h-5 text-amber-500" />Aguardando CNPJ
        </CardTitle>
        <CardDescription>
          Cadastros feitos sem o CPF/CNPJ. Quem está em <b>ativação assistida</b> já usa o plano liberado
          e recebe cobrança automática no WhatsApp (1h · 24h · 72h · 7d · 15d · 25d).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-3 flex-wrap">
          {[['Pendentes', data.total, 'bg-slate-100 text-slate-700'],
            ['Plano liberado', data.assisted, 'bg-amber-100 text-amber-800'],
            ['Prazo vencido', data.blocked, 'bg-rose-100 text-rose-700']].map(([label, val, cls]) => (
            <div key={label} className={`px-4 py-2 rounded-xl ${cls}`} data-testid={`pending-docs-kpi-${label}`}>
              <p className="text-xl font-bold leading-none">{val || 0}</p>
              <p className="text-[11px] mt-1">{label}</p>
            </div>
          ))}
        </div>

        {loading && <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}

        {/* Ativação retroativa: liberar plano numa empresa que já existe */}
        <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/40 space-y-3" data-testid="activate-existing-box">
          <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <Rocket className="w-4 h-4 text-emerald-600" />Ativar plano nesta empresa (empresa que já existe)
          </p>
          <div className="relative">
            <input value={selectedCompany ? selectedCompany.name : search}
              onChange={e => { setSearch(e.target.value); setActivateForm({ ...activateForm, company_id: '' }); }}
              placeholder="Buscar empresa no plano Free (digite 2+ letras)"
              className="w-full h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white"
              data-testid="activate-company-search" />
            {filteredCompanies.length > 0 && !selectedCompany && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                {filteredCompanies.map(c => (
                  <button key={c.id} onClick={() => setActivateForm({ ...activateForm, company_id: c.id })}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                    data-testid={`activate-company-option-${c.slug}`}>
                    {c.name} <span className="text-[11px] text-slate-400">· {c.owner_email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Plano</label>
              <select value={activateForm.plan_id} onChange={e => setActivateForm({ ...activateForm, plan_id: e.target.value })}
                className="h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white" data-testid="activate-plan-select">
                {PAID_PLANS.map(p => <option key={p.id} value={p.id}>{p.label} · R$ {p.price.toFixed(2).replace('.', ',')}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Prazo p/ o CNPJ</label>
              <select value={activateForm.deadline_days} onChange={e => setActivateForm({ ...activateForm, deadline_days: Number(e.target.value) })}
                className="h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white" data-testid="activate-deadline-select">
                <option value={7}>7 dias</option>
                <option value={15}>15 dias</option>
                <option value={30}>30 dias</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-700 h-9">
              <input type="checkbox" checked={activateForm.block_enabled}
                onChange={e => setActivateForm({ ...activateForm, block_enabled: e.target.checked })}
                data-testid="activate-block-checkbox" />
              Bloquear painel ao fim do prazo
            </label>
            <Button size="sm" onClick={activate} disabled={activating || !activateForm.company_id}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-xs" data-testid="activate-confirm-btn">
              {activating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Ativar plano'}
            </Button>
          </div>
          <p className="text-[11px] text-slate-500">
            A empresa entra na régua automática de cobrança do CNPJ. Sem a checkbox marcada, o painel nunca é bloqueado — só aparece o banner.
          </p>
        </div>

        {!loading && data.items.length === 0 && (
          <div className="py-10 text-center text-sm text-slate-500 flex flex-col items-center gap-2" data-testid="pending-docs-empty">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
            Nenhum cadastro aguardando CNPJ.
          </div>
        )}

        {!loading && data.items.map(item => (
          <div key={item.company_id} data-testid={`pending-doc-row-${item.slug}`}
            className={`p-4 rounded-xl border ${item.blocked ? 'bg-rose-50/60 border-rose-200' : item.assisted ? 'bg-amber-50/50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">
                  {item.name}
                  <span className={`ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${item.assisted ? 'bg-amber-200 text-amber-900' : 'bg-slate-200 text-slate-600'}`}>
                    {item.assisted ? 'PLANO LIBERADO' : 'PAROU NO PASSO 2'}
                  </span>
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {item.plan_name} · R$ {(item.plan_price || 0).toFixed(2).replace('.', ',')}/mês ·
                  {' '}{item.whatsapp || 'sem WhatsApp'} · esperando há {item.days_waiting ?? 0} dia(s) ·
                  {' '}{item.nudges_sent} mensagem(ns) enviada(s)
                  {item.assisted && (item.blocked
                    ? ' · PRAZO VENCIDO (painel bloqueado)'
                    : item.days_left !== null
                      ? ` · ${item.days_left} dia(s) de prazo${item.block_enabled ? ' · bloqueia no fim' : ' · sem bloqueio'}`
                      : '')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => copyLink(item.resume_url)} title="Copiar link de conclusão"
                  className="p-2 rounded-lg bg-white border border-slate-200 text-slate-500 hover:text-[#C5A059]"
                  data-testid={`pending-doc-copy-${item.slug}`}><Copy className="w-3.5 h-3.5" /></button>
                <Button size="sm" variant="outline" disabled={busy === item.company_id || !item.whatsapp}
                  onClick={() => resend(item)} className="h-9 gap-1.5 text-xs"
                  data-testid={`pending-doc-resend-${item.slug}`}>
                  {busy === item.company_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Reenviar link
                </Button>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-200/70" data-testid={`pending-doc-history-${item.slug}`}>
              <p className="text-[11px] font-semibold text-slate-600 mb-1.5">Histórico de cobrança</p>
              {(item.messages || []).length === 0 ? (
                <p className="text-[11px] text-slate-400">Nenhuma mensagem enviada ainda.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {item.messages.map(m => (
                    <span key={m.tag} data-testid={`pending-doc-msg-${item.slug}-${m.tag}`}
                      className={`text-[10px] px-2 py-1 rounded-full border ${m.kind === 'aviso' ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-white border-slate-200 text-slate-600'}`}>
                      {TAG_LABELS[m.tag] || m.tag} · {fmtDateTime(m.sent_at)}
                    </span>
                  ))}
                </div>
              )}
              {item.next_message && (
                <p className="text-[11px] text-slate-500 mt-1.5" data-testid={`pending-doc-next-${item.slug}`}>
                  Próxima: <b>{TAG_LABELS[item.next_message.tag] || item.next_message.tag}</b> em {fmtDateTime(item.next_message.due_at)}
                </p>
              )}
            </div>
            <div className="flex items-end gap-2 mt-3">
              <input value={docInputs[item.company_id] || ''} placeholder="CPF ou CNPJ do cliente" inputMode="numeric"
                onChange={e => setDocInputs({ ...docInputs, [item.company_id]: e.target.value })}
                className="flex-1 min-w-[160px] h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white"
                data-testid={`pending-doc-input-${item.slug}`} />
              <select value={dueDays[item.company_id] || ''}
                onChange={e => setDueDays({ ...dueDays, [item.company_id]: e.target.value })}
                className="h-9 px-2 border border-slate-300 rounded-lg text-xs bg-white"
                data-testid={`pending-doc-due-${item.slug}`}>
                <option value="">Vencimento padrão</option>
                <option value="3">Vence em 3 dias</option>
                <option value="7">Vence em 7 dias</option>
                <option value="15">Vence em 15 dias</option>
                <option value="30">Vence em 30 dias</option>
              </select>
              <Button size="sm" disabled={busy === item.company_id} onClick={() => complete(item)}
                className="bg-[#0A2647] hover:bg-[#0e3260] text-white h-9 text-xs"
                data-testid={`pending-doc-complete-${item.slug}`}>
                {busy === item.company_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Gerar cobrança'}
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default AdminPendingDocsTab;
